import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { OryOidcApi } from './api';
import type { OryGetOutput, OryExecuteInput, OryExecuteOutput } from './model';
import { OryIamConfig } from './model';
import type { OAuth2LoginRequest, AcceptOAuth2LoginRequest, OAuth2RedirectTo } from '@ory/client';

/**
 * Service for Ory Hydra OAuth2 login and consent operations.
 *
 * Handles OAuth2 authorization code grant flows:
 * 1. User authenticates (Kratos login)
 * 2. Hydra may redirect to login screen with challenge (for prompt=none)
 * 3. Hydra redirects to consent screen with challenge
 * 4. User grants/denies scopes
 * 5. App calls accept/reject, Hydra redirects back to client
 *
 * @remarks
 * This service is intentionally separate from OryService (Kratos) because
 * OAuth2 flows have a fundamentally different lifecycle:
 * - Challenge-based (server-initiated), not client-created
 * - Binary outcome (accept/reject), not form submission
 * - Always results in external redirect
 *
 * Uses the same model types as OryService (OryGetOutput, OryExecuteInput, OryExecuteOutput)
 * for consistency across the library.
 *
 * @example
 * ```typescript
 * // In login guard (for prompt=none)
 * const loginRequest = await oauthService.getLoginRequest(challenge);
 * if (loginRequest.skip) {
 *   oauthService.acceptLogin(loginRequest, { subject: loginRequest.subject })
 *     .subscribe(response => window.location.href = response.redirect_to);
 * }
 *
 * // In consent guard/resolver
 * const request = await oauthService.getConsentRequest(challenge);
 * oauthService.acceptConsent(request, { grant_scope: ['openid', 'profile'] })
 *   .subscribe(response => window.location.href = response.redirect_to);
 * ```
 */
@Injectable({ providedIn: 'root' })
export class OryOAuthService {
  readonly #oidcApi = inject(OryOidcApi);
  readonly #config = inject(OryIamConfig);

  /**
   * Retrieves the OAuth2 consent request details.
   *
   * Called when user lands on consent page with `?consent_challenge=...`
   * Returns client info, requested scopes, and user subject.
   *
   * @param challenge - The consent challenge from URL query parameter
   * @returns Observable of consent request with client and scope details
   */
  getConsentRequest(challenge: string): Observable<OryGetOutput<'oauth2_consent'>> {
    return this.#oidcApi.getConsentRequest({ consentChallenge: challenge });
  }

  /**
   * Accepts the consent request, granting scopes to the OAuth2 client.
   *
   * @param request - The consent request object from getConsentRequest
   * @param input - Accept input (AcceptOAuth2ConsentRequest from Ory SDK)
   * @returns Observable of redirect response (caller handles redirect)
   *
   * @throws Error if request.subject is missing (required for ID token claims)
   */
  acceptConsent(
    request: OryGetOutput<'oauth2_consent'>,
    input: OryExecuteInput<'oauth2_consent_accept'>
  ): Observable<OryExecuteOutput<'oauth2_consent_accept'>> {
    if (!request.subject) {
      throw new Error(
        'Cannot accept consent: subject is missing from OAuth2ConsentRequest. ' +
        'Subject is required to enrich ID tokens with user claims.'
      );
    }

    // Add remember_for duration if remember is true and configured
    const requestData = {
      ...input,
      remember_for: input.remember ? this.#config.oidc.consentRememberSeconds : undefined,
    };

    return this.#oidcApi.acceptConsentRequest(
      request.challenge,
      requestData,
      request.subject
    );
  }

  /**
   * Rejects the consent request, denying access to the OAuth2 client.
   *
   * @param request - The consent request object from getConsentRequest
   * @param input - Reject input (RejectOAuth2Request from Ory SDK)
   * @returns Observable of redirect response (caller handles redirect)
   */
  rejectConsent(
    request: OryGetOutput<'oauth2_consent'>,
    input: OryExecuteInput<'oauth2_consent_reject'> = { error: 'access_denied' }
  ): Observable<OryExecuteOutput<'oauth2_consent_reject'>> {
    return this.#oidcApi.rejectConsentRequest(
      request.challenge,
      input
    );
  }

  /* ========================================
   * Login Request Methods
   * ========================================
   */

  /**
   * Retrieves the OAuth2 login request details.
   *
   * @remarks
   * Called when Hydra redirects to login page with `?login_challenge=...`
   *
   * The response includes:
   * - skip: true if Hydra has an existing login session (created by previous remember=true)
   * - subject: User ID if skip=true
   * - client: OAuth2 client details
   *
   * Hydra evaluates prompt, max_age, id_token_hint, and existing login session
   * to determine whether skip=true (login UI can be skipped).
   *
   * @param challenge - The login challenge from URL query parameter
   * @returns Observable of login request details
   */
  getLoginRequest(challenge: string): Observable<OAuth2LoginRequest> {
    return this.#oidcApi.getLoginRequest(challenge);
  }

  /**
   * Accepts the login request.
   *
   * @remarks
   * When accepting with remember=true, Hydra creates/refreshes an OAuth2 login session
   * for this user+client combination. Future authorization requests with prompt=none
   * will then receive skip=true in the login request, enabling silent authentication.
   *
   * When remember=false, no login session is created, and prompt=none requests will
   * fail with login_required error.
   *
   * @param challenge - The login challenge from the login request
   * @param input - Accept input (subject, remember, ACR, extend_session_lifespan, etc.)
   * @returns Observable of redirect response (caller handles redirect)
   */
  acceptLogin(
    challenge: string,
    input: AcceptOAuth2LoginRequest
  ): Observable<OAuth2RedirectTo> {
    // Add remember_for duration if remember is true and configured
    const requestData: AcceptOAuth2LoginRequest = {
      ...input,
      remember_for: input.remember ? this.#config.oidc.loginRememberSeconds : undefined,
    };

    return this.#oidcApi.acceptLoginRequest(challenge, requestData);
  }

  /**
   * Rejects the login request.
   *
   * @param request - The login request object from getLoginRequest
   * @param input - Reject input (error code and description)
   * @returns Observable of redirect response (caller handles redirect)
   */
  rejectLogin(
    request: OAuth2LoginRequest,
    input: OryExecuteInput<'oauth2_consent_reject'> = { error: 'access_denied' }
  ): Observable<OAuth2RedirectTo> {
    return this.#oidcApi.rejectLoginRequest(
      request.challenge,
      input
    );
  }

  /**
   * Revokes all OAuth2 login sessions for a subject.
   *
   * @remarks
   * Terminates all active OAuth2 login sessions for a user across all OAuth clients.
   * Called during logout to clear remembered authentication decisions.
   *
   * OAuth2 login sessions are created when accepting login requests with
   * `remember=true`. These persistent sessions (default: 30 days) enable
   * automatic re-authentication via skip flow. Revoking them forces full
   * authentication on the next OAuth flow, ensuring logout integrity.
   *
   * Delegates to OryOidcApi which forwards to Lambda backend.
   */
  revokeLoginSessions(subject: string): Observable<void> {
    return this.#oidcApi.revokeLoginSessions(subject);
  }

  /* ========================================
   * Logout Request Methods
   * ========================================
   */

  /**
   * Retrieves the OAuth2 logout request details.
   *
   * @remarks
   * Called when user lands on logout page with `?logout_challenge=...`
   * (Hydra redirects here during RP-Initiated Logout).
   *
   * @param challenge - The logout challenge from URL query parameter
   * @returns Observable of logout request with subject, SID, client details
   */
  getLogoutRequest(challenge: string): Observable<OryGetOutput<'oauth2_logout'>> {
    return this.#oidcApi.getLogoutRequest(challenge);
  }

  /**
   * Accepts the logout request.
   *
   * @remarks
   * Returns redirect_to containing the logout_verifier URL. The browser
   * MUST follow this redirect for Hydra to invalidate the session and
   * trigger front/back-channel logout notifications.
   *
   * @param challenge - The logout challenge
   * @returns Observable of redirect response with logout_verifier URL
   */
  acceptLogout(challenge: string): Observable<OryExecuteOutput<'oauth2_logout_accept'>> {
    return this.#oidcApi.acceptLogoutRequest(challenge);
  }

  /**
   * Rejects the logout request.
   *
   * @param challenge - The logout challenge
   * @returns Observable that completes when rejection is processed
   */
  rejectLogout(challenge: string): Observable<OryExecuteOutput<'oauth2_logout_reject'>> {
    return this.#oidcApi.rejectLogoutRequest(challenge);
  }

  /**
   * Revokes OAuth2 consent sessions for a subject.
   *
   * @remarks
   * Invalidates issued OAuth2 access/refresh tokens. When clientId is provided,
   * only tokens for that client are invalidated.
   *
   * This is an authorization revocation API, separate from logout.
   *
   * @param subject - User ID whose consent sessions to revoke
   * @param clientId - Optional OAuth client ID for per-client revocation
   */
  revokeConsentSessions(subject: string, clientId?: string): Observable<void> {
    return this.#oidcApi.revokeConsentSessions(subject, clientId);
  }
}
